import "./survey.css";
import { cloneDeep, get, merge } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getI18n, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import { takeUIScreenViewEvent } from "../../../../../../common/ga/ga";
import { TakeUIScreenViewEventParameters } from "../../../../../../common/ga/types/ga-general-type";
import {
  ConfirmButton,
  CancelButton /* PHKL Custom */,
} from "../../../../../../components/buttons";
import { HeaderComponent } from "../../../../../../components/header/header.component";
import { confirm } from "../../../../../../components/modal/comfirm";
import { PruToast } from "../../../../../../components/pru-toast";
import { useCanGoBack } from "../../../../../../utils/hooks/use-can-go-back";
import { useQuery } from "../../../../../../utils/hooks/use-query";
import { selectRegistrationProfile, selectToken } from "../../../../../auth/redux/selector";
import { initFileUploadAndDownLoadAndClearForLicenseExam } from "../../../../components/surveyjs/file/upload-file";
import { initSurvey } from "../../../../components/surveyjs/survey/init-survey";
import { markdownHandler } from "../../../../components/surveyjs/survey/survey.util";
import { handleSurveyError } from "../../../../network/network.util";
import {
  fetchConfigAsync,
  fetchTrainingExamByIdAsync,
  fetchTrainingExamCentresAsync,
  fetchTrainingExamsAsync,
  fetchTrainingExamTimeslotsAsync,
  getOnboardingTrainingSummaryAsync,
  postCandidateExamRegistryAsync,
  resetTrainingExamTimeslots,
} from "../../../../redux/onboarding-slice";
import {
  selectEnableLicenseExamPayment,
  selectEnableProvideExistingLicense,
  selectOnboardingCurrentExam,
  selectOnboardingExamCentres,
  selectOnboardingRegisteredSchedules,
  selectOnboardingExamTimeslots,
  selectOnboardingTrainingSummary,
} from "../../../../redux/selector";
import {
  ExamStatus,
  PaymentStatus,
  PaymentType,
  RegistrationDTO,
  RoleEnum,
} from "../../../../types";
import { backToApp, getTargetCandidate, getToken, parseJwt } from "../../../../utils";
import { updateTargetCandidatePaymentType } from "../../../course-menu-screen/utils";
import { convertExamSurveyDataToRegistrationDTO } from "../../../form/util";
import { ExamTemplate as _ExamTemplate } from "./templateJson";
import ReminderTip from "./components/reminder-tip";
import { GlobalHelper } from "../../../../../../utils/helpers/global-helper";
import moment from "moment";
import { constants, ONBOARDING_PATH, ROOT_PATH } from "../../../../../../routes/constants";
import {
  getCandidateProfileAsync,
  updateCandidateProfileAsync,
} from "../../../../../auth/redux/auth-slice";
import {
  ExamMode,
  iconMap,
} from "../../../candidate-home/components/license-exam-entrance-card/type";
import { I18nModule, translate } from "../../../../../../i18n/translation";
import { getIdTypeOptions } from "../../../../../../utils/common-utils";
import styles from "./exam-registry-payment-screen.module.css";
import store from "../../../../../../redux/store";
// PHKL Custom -s
import {
  getCandidateExamApprovalVerify,
  putCandidateExamApproval,
  putCandidateExamReject,
  putCandidateExamCancel,
} from "../../../../network/network";
import { ArrowBack } from "@mui/icons-material";
import "moment/locale/zh-hk";
// PHKL Custom -s
import { CircularProgress } from "@mui/material";
import { color } from "../../../../../../mui-theme/palette";
// PHKL Custom -e

moment.updateLocale("zh-hk", {
  meridiem: function (hour, minute, isLower) {
    var hm = hour * 100 + minute;
    if (hm < 1200) {
      return "上午";
    } else {
      return "下午";
    }
  },
});
// PHKL Custom -e

const validateFields = ["birthday", "identityDocumentType", "identityDocumentNumber"];

export const ExamRegistryPaymentScreen: React.FC = () => {
  initSurvey();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery();
  const canGoBack = useCanGoBack();
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const momentLang = getI18n().language === "zhhk" ? "zh-hk" : "en"; // PHKL Custom

  const tokenData = useMemo(() => {
    const tokenFromStorage = getToken();
    const tokenPayload = parseJwt(tokenFromStorage);
    return merge(tokenPayload, token);
  }, [token]);

  const [globalConfigs, setGlobalConfigs] = useState({} as any);
  const [recruitConfigs, setRecruitConfigs] = useState({} as any);
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);

  const [formValues, setForm] = useState({} as any);
  const candidateId = query.get("candidateId") ?? GlobalHelper.getTokenUserSub();
  const [survey, setSurvey] = useState(undefined as any);
  const [currentPageNo, setCurrentPageNo] = useState(0);

  const [confirmButtonText, setConfirmButtonText] = useState<string>();
  const [examData, setExamData] = useState<RegistrationDTO>({});
  const [isApprovalPage, setIsApprovalPage] = useState<boolean>(false); // PHKL Custom
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false); // PHKL Custom
  const [surveyIsReadonly, setSurveyIsReadonly] = useState<boolean>(false);
  const candidateProfile = useSelector(selectRegistrationProfile);

  const currentExam = useSelector(selectOnboardingCurrentExam);
  const [pageInitiating, setPageInitiating] = useState(true);
  const enableLicenseExamPayment = useSelector(selectEnableLicenseExamPayment);
  const enableProvideExistingLicense = useSelector(selectEnableProvideExistingLicense);

  const candidateOnboardingTrainingSummary = useSelector(selectOnboardingTrainingSummary);

  const registeredScheduleIds = useSelector(selectOnboardingRegisteredSchedules);

  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>();

  const examCode = useParams().examId;
  const registrationId = query.get("registrationId");

  const examCentrePools = useSelector(selectOnboardingExamCentres);
  const examTimeslotPools = useSelector(selectOnboardingExamTimeslots);

  // PHKL Custom -s
  const filterIdType = (idType: any) => {
    if (!idType) {
      return null;
    }
    const existsDefaultKey = Object.keys(idType).some(
      (key) =>
        idType[key].module &&
        idType[key].module.includes("CandidateWeb.ExamRegistration") &&
        idType[key].isDefault,
    );
    let defaultKey = "";
    const filteredObj: any = {};
    Object.keys(idType).forEach((key, index) => {
      if (idType[key].module && idType[key].module.includes("CandidateWeb.ExamRegistration")) {
        if (!existsDefaultKey && !defaultKey) {
          defaultKey = key;
          filteredObj[key] = { ...idType[key], isDefault: true };
        } else {
          filteredObj[key] = idType[key];
        }
      }
    });
    return filteredObj;
  };
  // PHKL Custom -e

  const getIdFields = (globalConfigs: any, t: any) => {
    const idTypeConfigs = filterIdType(globalConfigs?.region?.idType); // PHKL Custom
    if (!idTypeConfigs) {
      return [];
    }
    const options = getIdTypeOptions(idTypeConfigs)?.options; // PHKL Custom

    return [
      {
        type: "dropdown",
        name: "identityDocumentType",
        title: t("Common.id_type"),
        isRequired: true,
        choices: options ?? [],
        placeholder: t("Please select"),
        requiredErrorText: t("Common.please_select_id_type"),
      },
      ...Object.keys(idTypeConfigs).map((key) => {
        const option = idTypeConfigs[key];
        return {
          type: "text",
          name: `identityDocumentNumber`,
          visibleIf: `{identityDocumentType}="${option.value}"`,
          title: t("Common.id_number"),
          isRequired: true,
          tooltips: option.desc ? t(option.desc.replace(":", ".")) : "",
          placeholder: t("Common.id_num_nric_placeholder"),
          validators: [
            {
              type: "expression",
              text: t(option.errMsg.replace(":", ".")),
              expression: "idNumValidate({#identityDocumentType},{identityDocumentNumber})",
            },
          ],
          inputType: "text",
          requiredErrorText: t("Common.id_num_nric_placeholder"),
        };
      }),
    ];
  };

  const generateFormUI = (elements: any, globalConfigs: any, t: any) => {
    // NOTE: idFieldStartIndex is the index where the id fields should be inserted, after the localName field
    const idFieldStartIndex = 5; // PHKL Custom
    const hasidentityDocumentTypeField = elements.some(
      (element: any) => element.name === "identityDocumentType",
    );

    if (!hasidentityDocumentTypeField) {
      const idFields = getIdFields(globalConfigs, t);
      elements.splice(idFieldStartIndex, 0, ...idFields);
    }
  };
  const ExamTemplate = useMemo(() => {
    const template = convertTemplate(_ExamTemplate, recruitConfigs, globalConfigs);
    const elements = template.pages[2].elements;
    generateFormUI(elements, globalConfigs, t);
    return template;
  }, [globalConfigs, recruitConfigs]);

  const getPaymentType = () => {
    const targetCandidate = getTargetCandidate();
    return targetCandidate.paymentType;
  };
  const getCandidateStutus = () => {
    const targetCandidate = getTargetCandidate();
    return targetCandidate.status;
  };
  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchData = useCallback(async () => {
    if (!(registrationId || examCode) || !candidateId) return;
    const [configsResponse, _, __] = await Promise.all([
      dispatch<any>(fetchConfigAsync()),
      dispatch<any>(fetchTrainingExamsAsync({ candidateId })),
      dispatch<any>(
        fetchTrainingExamByIdAsync({
          candidateId,
          registrationId: registrationId,
          examCode: examCode!,
        }),
      ),
    ]);

    const recruitConfig = get(configsResponse, "payload.data.Recruitment", {});
    const globalConfig = get(configsResponse, "payload.data.Global", {});

    setRecruitConfigs(recruitConfig);
    setGlobalConfigs(globalConfig);
    GlobalHelper.setIdTypeConfigs(globalConfig?.region?.idType);

    dispatch<any>(getOnboardingTrainingSummaryAsync({ candidateId }));
    setPageInitiating(false);
  }, [candidateId, dispatch, examCode, registrationId]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  useEffect(() => {
    if (candidateOnboardingTrainingSummary?.trainingSummary) {
      setPaymentStatus(candidateOnboardingTrainingSummary?.trainingSummary?.paymentStatus);
    }
  }, [candidateOnboardingTrainingSummary]);
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        await dispatch<any>(getCandidateProfileAsync(candidateId));
      } catch (error) {
        console.error("Failed to fetch candidate profile:", error);
      }
    };

    fetchProfile();
  }, [dispatch, candidateId]);

  const fetchExamCentres = useCallback(async () => {
    if (!currentExam) return;
    await dispatch<any>(fetchTrainingExamCentresAsync(currentExam));
  }, [currentExam, dispatch]);

  const initPersonalForm = () => {
    // PHKL Custom -s
    const arrPhone = candidateProfile?.phone?.split(" ") || [];

    return {
      ...formValues,
      agentCode: candidateProfile?.agentid,
      firstName: candidateProfile?.firstname,
      lastName: candidateProfile?.lastname,
      gender: candidateProfile?.gender,
      localName: candidateProfile?.localName,
      noChineseName: !!candidateProfile?.noChineseName, // PHKL Custom
      birthday: candidateProfile?.birthday,
      identityDocumentType: candidateProfile?.identityDocumentType,
      identityDocumentNumber: candidateProfile?.identityDocumentNumber,
      email: candidateProfile?.email,
      regionPhone: [
        {
          regionCode: arrPhone[0] || "",
          phoneNumber: arrPhone[1] || "",
        },
      ],

      languagePreference: candidateProfile?.languagePreference,
    };
    // PHKL Custom -e
  };

  // PHKL Custom -s
  const initCommonDate = (date?: string) => {
    if (!date) {
      return;
    }
    return moment(date)
      .locale(momentLang)
      .format(t("new_iiqe_registration_exam_schedule_common_date_format"))
      .replaceAll("00分", "");
  };
  // PHKL Custom -e

  useEffect(() => {
    setIsApprovalPage(false); // PHKL Custom
    if (!currentExam) {
      return;
    }
    const personalForm = initPersonalForm();
    const canEdit = query.get("canEdit") === "true";

    if (canEdit) {
      const initFormData: any = {
        ...personalForm,
        hasPassedExam: enableProvideExistingLicense ? "" : false,
        paymentType: getPaymentType(),
      };
      setForm(initFormData);
      setSurveyIsReadonly(false);
    } else {
      // PHKL Custom -s
      setIsApprovalPage(
        currentExam?.originData?.submittedByRole === "AGENT" &&
          currentExam?.originData?.registrationApprovalStates === "PENDING_APPROVAL",
      );
      // PHKL Custom -e

      const currencies = get(store.getState(), "onboarding.configs.Global.region.currencies", []);
      const currencySymbol = currencies.length
        ? currencies.find(
            (currency: any) =>
              currency.code === (currentExam?.originData as any)?.examFeeCurrencyCode,
          )?.symbol ?? currencies[0].symbol
        : "";
      setForm({
        ...personalForm,
        hasPassedExam: currentExam?.originData?.hasPassedExam,
        hasLicenseProof: currentExam?.originData?.licenseInfo?.hasLicenseProof,
        examCentre: currentExam?.originData?.examSchedule?.examCentre,
        examScheduleId: currentExam?.originData?.examSchedule?.id,
        paymentType: getPaymentType(),
        examScore: currentExam?.originData?.licenseInfo?.examScore,
        examDate: currentExam?.originData?.licenseInfo?.examDate,
        officialReceiptNumber: currentExam?.originData?.licenseInfo?.officialReceiptNumber,
        paymentReceiptDate: currentExam?.originData?.licenseInfo?.paymentReceiptDate,
        proofInfos: currentExam?.originData?.licenseInfo?.proofInfos,
        licenseFeeReceiptNo: currentExam?.originData?.examPayment?.licenseFeeReceiptNo,
        licenseFeeReceiptDate: moment(
          currentExam?.originData?.examPayment?.licenseFeeReceiptDate,
        ).format(constants.Date_Minute_Scale_Format),
        examFeeReceiptNo: currentExam?.originData?.examPayment?.examFeeReceiptNo,
        examFeeReceiptDate: moment(currentExam?.originData?.examPayment?.examFeeReceiptDate).format(
          constants.Date_Minute_Scale_Format,
        ),
        remainingQuota: currentExam?.originData?.examSchedule?.remainingQuota,
        examFee: currentExam?.originData?.examSchedule?.examFee
          ? `${currencySymbol}${currentExam?.originData?.examSchedule?.examFee}`
          : "",
        startRegistrationDate: initCommonDate(
          currentExam?.originData?.examSchedule?.startRegistrationDate,
        ), // PHKL Custom
        lastRegistrationTime: initCommonDate(
          currentExam?.originData?.examSchedule?.lastRegistrationDate,
        ), // PHKL Custom
        address: currentExam?.originData?.examSchedule?.address,
        remarks: currentExam?.originData?.examSchedule?.remarks,
      });
      setSurveyIsReadonly(true);
    }

    if (tokenData.role !== RoleEnum.CANDIDATE) {
      setSurveyIsReadonly(true);
    }
    // if (getCandidateStutus() !== CandidateStatus.IN_PROGRESS) {
    //   setSurveyIsReadonly(true);
    // }
  }, [currentExam, enableProvideExistingLicense, tokenData.role]);

  //PHKL Custom -s
  const formatExamDate = (
    examDate: any,
    examStartTime: any,
    examEndTime: any,
    dateFormat?: string,
    isNeedMerge?: boolean,
  ) => {
    const scheduleFormat = {
      initDate: dateFormat ? `${dateFormat} hh:mm` : "YYYY-MM-DD hh:mm",
      date: t("new_iiqe_registration_exam_schedule_start_date_format"),
      start: t("new_iiqe_registration_exam_schedule_start_time_format"),
      end: t("new_iiqe_registration_exam_schedule_end_time_format"),
    };
    const momentLang = getI18n().language === "zhhk" ? "zh-hk" : "en";
    const dateTime = moment(`${examDate} ${examStartTime}`, scheduleFormat.initDate).locale(
      momentLang,
    );
    const startTime = dateTime.format(scheduleFormat.start);
    const endTime = moment(`${examDate} ${examEndTime}`, scheduleFormat.initDate)
      .locale(momentLang)
      .format(scheduleFormat.end);
    const result = startTime.replace("{{}}", endTime).replaceAll("00分", "");
    return {
      date: `${dateTime.format(scheduleFormat.date)} ${isNeedMerge ? result : ""}`,
      time: result,
    };
  };
  //PHKL Custom -e

  useEffect(() => {
    if (!survey) {
      return;
    }
    const examCentreQuestion = survey.getQuestionByName("examCentre");
    const examRegistrationIdQuestion = survey.getQuestionByName("examScheduleId");

    var centreChoices;
    var timeChoices;
    if (!surveyIsReadonly) {
      centreChoices = examCentrePools.map(function (pool) {
        return {
          value: pool.examCentre,
          text: pool.examCentre,
        };
      });
      timeChoices = examTimeslotPools.map(function (pool, index) {
        // PHKL Custom
        const { date, time } = formatExamDate(
          pool?.examDate,
          pool?.examStartTime,
          pool?.examEndTime,
          "DD/MM/YYYY",
        ); //PHKL Custom;
        return {
          ...pool,
          examMode: [ExamMode.COMPUTER, ExamMode.PAPER].includes(pool?.examMode as ExamMode)
            ? pool.examMode
            : null,
          examModeIcon: iconMap[pool?.examMode as keyof typeof iconMap],
          deviceType: pool?.deviceType,
          disabled: registeredScheduleIds.includes(pool.value),
          text: date, // PHKL Custom
          underText: time, // PHKL Custom
          isLastIndex: index === examTimeslotPools?.length - 1, // PHKL Custom
        };
      });
    } else {
      const examSchedule = currentExam?.originData?.examSchedule;
      const { date } = formatExamDate(
        examSchedule?.examDate,
        examSchedule?.startTime,
        examSchedule?.endTime,
        undefined,
        true,
      ); //PHKL Custom;
      centreChoices = [
        {
          value: examSchedule?.examCentre,
          text: examSchedule?.examCentre,
        },
      ];
      timeChoices = [
        {
          ...examSchedule,
          examMode: [ExamMode.COMPUTER, ExamMode.PAPER].includes(examSchedule?.examMode as ExamMode)
            ? examSchedule?.examMode
            : null,
          examModeIcon: iconMap[examSchedule?.examMode as keyof typeof iconMap],
          deviceType: examSchedule?.deviceType,
          disabled: registeredScheduleIds.includes(examSchedule?.id),
          value: examSchedule?.id,
          text: date, //PHKL Custom
        },
      ];
    }
    examCentreQuestion.choices = centreChoices;
    examRegistrationIdQuestion.choices = timeChoices;

    // PHKL Custom s
    const canEdit = query.get("canEdit") === "true";
    if (canEdit && centreChoices && centreChoices.length === 1) {
      examCentreQuestion.value = centreChoices[0].value;
    }
    if (
      canEdit &&
      timeChoices &&
      timeChoices.length === 1 &&
      !registeredScheduleIds.includes(timeChoices[0].value)
    ) {
      examRegistrationIdQuestion.value = timeChoices[0].value;
      const readOnlyText = (name: any, survey: any) => {
        if (survey) survey.getQuestionByName(name).readOnly = true;
      };
      const readOnlylist = [
        "remainingQuota",
        "examFee",
        "startRegistrationDate",
        "lastRegistrationTime",
        "address",
        "remarks",
      ];
      readOnlylist.forEach((item) => {
        readOnlyText(item, survey);
      });
    }
    // PHKL Custom e
  }, [
    currentExam,
    examCentrePools,
    examTimeslotPools,
    survey,
    surveyIsReadonly,
    registeredScheduleIds,
  ]);

  useEffect(() => {
    fetchExamCentres();
  }, [fetchExamCentres]);

  useEffect(() => {
    //the browser back event
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      if (survey.currentPageNo === 2 && enableLicenseExamPayment) {
        survey.currentPageNo = 1;
      } else if (survey.currentPageNo > 0) {
        survey.currentPageNo = 0;
      } else {
        navigate(-1);
      }
    };
    window.onpopstate = handlePopState;
    return () => {
      window.onpopstate = null;
    };
  }, [enableLicenseExamPayment, navigate, survey]);

  useEffect(() => {
    if (!survey) {
      return;
    }
    if (currentExam && (examData.examCentre || formValues.examCentre)) {
      dispatch<any>(
        fetchTrainingExamTimeslotsAsync({
          ...currentExam,
          examCentre: examData.examCentre || formValues.examCentre,
        } as any),
      );
    } else {
      dispatch(resetTrainingExamTimeslots());
    }
    const examRegistrationIdQuestion = survey.getQuestionByName("examScheduleId");
    examRegistrationIdQuestion.value = "";
  }, [currentExam, dispatch, examData.examCentre, formValues.examCentre, survey]);

  const updateCandidateProfile = useCallback(
    async (examData: any) => {
      const candidateData: any = {};
      candidateData.gender = examData.gender;
      candidateData.firstname = examData.firstName;
      candidateData.lastname = examData.lastName;
      candidateData.localName = examData.noChineseName ? "" : examData.localName; // PHKL Custom
      candidateData.noChineseName = examData.noChineseName; // PHKL Custom
      candidateData.birthday = moment(examData.birthday).format(constants.Default_Date_Format);
      candidateData.identityDocumentType = examData.identityDocumentType;
      candidateData.identityDocumentNumber = examData.identityDocumentNumber;
      candidateData.email = examData.email;
      candidateData.regionCode = examData.regionPhone[0].regionCode; // PHKL Custom
      candidateData.phoneNumber = examData.regionPhone[0].phoneNumber;
      candidateData.languagePreference = examData.languagePreference ?? "en";
      candidateData.agentCode = examData.agentCode;

      const action = await dispatch<any>(
        updateCandidateProfileAsync({
          candidateData: { ...candidateProfile, ...candidateData },
        }),
      );

      if (updateCandidateProfileAsync.rejected.match(action)) {
        const error = action.payload || action.error;
        throw error;
      }
    },
    [candidateProfile, dispatch],
  );

  const onSubmitHandler = useCallback(
    (examData: any) => {
      const examTime = examTimeslotPools.find((item) => item.value === examData.examScheduleId);
      const { date, time } = formatExamDate(
        examTime?.examDate,
        examTime?.examStartTime,
        examTime?.examEndTime,
        "DD/MM/YYYY",
        true,
      ); // PHKL Custom
      confirm({
        message: t("iiqe_popup_please_note"), //PHKL Custom
        title: t("iiqe_reminder_title"), //PHKL Custom
        confirmButtonText: t("global.text.confirm"),
        messageComponent: (
          <ReminderTip
            details={[
              ...(!examData.hasPassedExam
                ? [
                    { text: `${t("iiqe_popup_exam_paper")}: ${currentExam?.name}` }, //PHKL Custom
                    { text: `${t("iiqe_popup_exam_venue")}: ${examData.examCentre}` }, //PHKL Custom
                    { text: `${t("iiqe_popup_exam_date")}: ${date}` }, //PHKL Custom
                    ...([ExamMode.COMPUTER, ExamMode.PAPER].includes(examTime?.examMode as ExamMode)
                      ? [
                          {
                            title: `${t("iiqe_popup_exam_mode")}: `, //PHKL Custom
                            text: `${translate(examTime?.examMode as ExamMode, I18nModule.Common)}`,
                            icon: examTime?.examMode,
                          },
                        ]
                      : []),
                    { text: `${t("iiqe_popup_quota")}: ${examTime?.remainingQuota}` }, //PHKL Custom
                  ]
                : []),
              ...(examData.hasPassedExam
                ? [
                    { text: `${t("license")}: ${currentExam?.name}` },
                    {
                      text: `${t("existingLicense")}: ${t("app.select.yes")}`,
                    },
                    {
                      text: `${t("proof")}: ${
                        examData.hasLicenseProof ? t("app.select.yes") : t("app.select.no")
                      }`,
                    },
                    {
                      text: `${t("examProofCopy")}: ${
                        examData.hasLicenseProof ? t("uploaded") : t("requested")
                      }`,
                    },
                  ]
                : []),
            ]}
          />
        ),
        messageStyle: { textAlign: "left" },
        //PHKL Custom -s
        onCancel: () => {
          setIsSubmitting(false);
        },
        //PHKL Custom -e
      }).then(async (result) => {
        if (result.ok) {
          try {
            await updateCandidateProfile(examData);
            const actionResponse = await dispatch<any>(
              postCandidateExamRegistryAsync({
                candidateId: candidateId as string,
                body: convertExamSurveyDataToRegistrationDTO(currentExam, examData, false),
              }),
            );
            if (postCandidateExamRegistryAsync.rejected.match(actionResponse)) {
              const error = actionResponse.payload || actionResponse.error;
              throw error;
            } else {
              if (examData.paymentType) {
                updateTargetCandidatePaymentType(examData.paymentType);
              }
              PruToast({
                status: "success",
                message: t("Registration Submitted"), //PHKL Custom
              });
              navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/candidate-home`);
              backToApp();
              setIsSubmitting(false); // PHKL Custom
            }
          } catch (error: any) {
            setIsSubmitting(false);
            const { statusCode, errors, message } = error as any;

            if (statusCode === 406) {
              PruToast({ status: "error", message: t("exam_registration_validation_error") });
              if (errors?.length) {
                const errorField = errors[0]?.code;
                if (errorField && validateFields.includes(errorField)) {
                  setForm((prev: any) => {
                    const current = prev || {};
                    return {
                      ...current,
                      [errorField]: null,
                    };
                  });
                }
              }
            } else {
              handleSurveyError({ errors, survey, defaultErrMsg: message ?? "Unknown error" });
            }
          }
        }
      });
    },
    [
      examTimeslotPools,
      t,
      currentExam,
      dispatch,
      candidateId,
      survey,
      updateCandidateProfile,
      navigate,
    ],
  );
  const validateData = useCallback(
    async (examData: any) => {
      const actionResponse = await dispatch<any>(
        postCandidateExamRegistryAsync({
          candidateId: candidateId as string,
          body: convertExamSurveyDataToRegistrationDTO(currentExam, examData, true),
        }),
      );

      const errors = actionResponse?.error ?? actionResponse?.payload?.errors;

      if (errors) {
        const message = errors.message ?? "Unknown error";
        handleSurveyError({ errors, survey, defaultErrMsg: message });
        return false;
      } else {
        return true;
      }
    },
    [currentExam, dispatch, candidateId, survey],
  );
  // PHKL Custom -s
  const onApproveVerify = async (examData: any) => {
    try {
      const approveRes = await getCandidateExamApprovalVerify(registrationId as string);
      if (typeof approveRes.data?.result === "boolean") {
        if (approveRes.data.result) {
          return true;
        } else {
          confirm({
            message: t("Recruitment.onboarding_exam_approve_error"),
            title: t("reminder_title"),
            confirmButtonText: t("Recruitment.onboarding_exam_approve_cancel_btn"),
            hideCancelButton: true,
            messageStyle: { textAlign: "left" },
          }).then(async (result) => {
            if (result.ok) {
              await onCancel();
            }
          });
        }
      } else {
        const errors = get(approveRes.data, "errors");
        const message = get(approveRes.data, "message", "Unknown error");
        handleSurveyError({ errors, survey, defaultErrMsg: message });
      }
    } catch (err) {
      const errors = get(err, "errors");
      const message = get(err, "message", "Unknown error");
      handleSurveyError({ errors, survey, defaultErrMsg: message });
    }
    return false;
  };
  const onApprove = async (examData: any) => {
    try {
      setIsSubmitting(false);
      await updateCandidateProfile(examData);
      const approveRes = await putCandidateExamApproval(registrationId as string);
      if (approveRes.data?.currentState === "REGISTERED") {
        PruToast({ status: "success", message: t("Registration Submitted") });
        navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/candidate-home`);
        backToApp();
      } else {
        const errors = get(approveRes.data, "errors");
        const message = get(approveRes.data, "message", "Unknown error");
        handleSurveyError({ errors, survey, defaultErrMsg: message });
      }
    } catch (err: any) {
      const { statusCode, errors } = err;
      if (statusCode === 406) {
        PruToast({ status: "error", message: t("exam_registration_validation_error") });
        if (errors?.length) {
          const errorField = errors[0]?.code;
          if (errorField && validateFields.includes(errorField)) {
            setForm((prev: any) => {
              const current = prev || {};
              return {
                ...current,
                [errorField]: null,
              };
            });
          }
        }
      } else {
        const errors = get(err, "errors");
        const message = get(err, "message", "Unknown error");
        handleSurveyError({ errors, survey, defaultErrMsg: message });
      }
    }
  };

  const onReject = async () => {
    try {
      await putCandidateExamReject(registrationId as string);
      PruToast({ status: "success", message: t("common.success") });
      navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/candidate-home`);
      backToApp();
    } catch (err) {
      const errors = get(err, "errors");
      const message = get(err, "message", "Unknown error");
      handleSurveyError({ errors, survey, defaultErrMsg: message });
    }
  };

  const onCancel = async () => {
    try {
      await putCandidateExamCancel(registrationId as string);
      PruToast({ status: "success", message: t("common.success") });
      navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/candidate-home`);
      backToApp();
    } catch (err) {
      const errors = get(err, "errors");
      const message = get(err, "message", "Unknown error");
      handleSurveyError({ errors, survey, defaultErrMsg: message });
    }
  };

  const approveOnSubmitHandler = useCallback(
    (examData: any) => {
      const examTime = currentExam?.originData?.examSchedule;
      const { date, time } = formatExamDate(
        examTime?.examDate,
        examTime?.startTime,
        examTime?.endTime,
        undefined,
        true,
      ); // PHKL Custom
      confirm({
        message: t("iiqe_popup_please_note_approve"),
        title: t("iiqe_reminder_title"),
        confirmButtonText: t("global.text.confirm"),
        messageComponent: (
          <ReminderTip
            details={[
              ...(!examData.hasPassedExam
                ? [
                    { text: `${t("iiqe_popup_exam_paper")}: ${currentExam?.name}` },
                    { text: `${t("iiqe_popup_exam_venue")}: ${examData.examCentre}` },
                    { text: `${t("iiqe_popup_exam_date")}: ${date}` }, //PHKL Custom
                    ...([ExamMode.COMPUTER, ExamMode.PAPER].includes(examTime?.examMode as ExamMode)
                      ? [
                          {
                            title: `${t("iiqe_popup_exam_mode")}: `, //PHKL Custom
                            text: ` ${translate(
                              examTime?.examMode as ExamMode,
                              I18nModule.Common,
                            )}`,
                            icon: examTime?.examMode,
                          },
                        ]
                      : []),
                    { text: `${t("iiqe_popup_quota")}: ${examTime?.remainingQuota}` },
                  ]
                : []),
              ...(examData.hasPassedExam
                ? [
                    { text: `${t("license")}: ${currentExam?.name}` },
                    {
                      text: `${t("existingLicense")}: ${t("app.select.yes")}`,
                    },
                    {
                      text: `${t("proof")}: ${
                        examData.hasLicenseProof ? t("app.select.yes") : t("app.select.no")
                      }`,
                    },
                    {
                      text: `${t("examProofCopy")}: ${
                        examData.hasLicenseProof ? t("uploaded") : t("requested")
                      }`,
                    },
                  ]
                : []),
            ]}
          />
        ),
        messageStyle: { textAlign: "left" },
      }).then(async (result) => {
        if (result.ok) {
          onApprove(examData);
        }
      });
    },
    [
      examTimeslotPools,
      t,
      currentExam,
      dispatch,
      candidateId,
      survey,
      updateCandidateProfile,
      navigate,
    ],
  );
  // PHKL Custom -e

  useEffect(() => {
    if (!survey) return;
    const isLastPage = currentPageNo === survey.visiblePageCount - 1;
    const isExamPassed = examData?.hasPassedExam;

    let buttonText;
    // PHKL Custom -s
    if (isApprovalPage) {
      switch (survey.currentPageNo) {
        case 0:
          buttonText = t("Common.confirm");
          break;
        case 1:
          buttonText = t("app.button.next");
          break;
        case 2:
          buttonText = t("register");
          break;
      }
    } else if (surveyIsReadonly) {
      buttonText = t("Common.close");
      // PHKL Custom -e
    } else if (!isExamPassed && !isLastPage) {
      buttonText = t("app.button.next");
    } else {
      if (surveyIsReadonly) {
        buttonText = t("Common.close");
      } else {
        buttonText = t("register");
      }
    }
    setConfirmButtonText(buttonText);
  }, [
    currentPageNo,
    t,
    examData?.hasPassedExam,
    enableLicenseExamPayment,
    survey,
    surveyIsReadonly,
  ]);
  useEffect(() => {
    setSurvey(new Model(ExamTemplate));
    const gaData: TakeUIScreenViewEventParameters = {
      module: "Recruitment",
      feature: "License",
      journey: "view_individual_license",
      stage: "journey_landing",
      screen_id: "SCR_RCRTMT_LCNS_DTLS",
      screen_name: "Recruitment-License-Details-Screen",
    };
    takeUIScreenViewEvent(gaData);
  }, [ExamTemplate]);

  useEffect(() => {
    if (!survey) {
      return;
    }
    const hasPassedExamQuestion = survey.getQuestionByName("hasPassedExam");
    hasPassedExamQuestion.visible = enableProvideExistingLicense;

    const paymentType = survey.getQuestionByName("paymentType");
    const examStatus = currentExam?.examResult;

    const licenseFeeReceiptNoQuestion = survey.getQuestionByName("licenseFeeReceiptNo");
    const paymentTypeQuestion = survey.getQuestionByName("licenseFeeReceiptDate");
    const isExamFailedButCodeNowLater =
      examStatus === ExamStatus.FAIL &&
      currentExam?.originData?.examPayment?.paymentType === PaymentType.CodeNowPayLater;
    const flag =
      paymentType.value === PaymentType.FullPayment &&
      (examStatus !== ExamStatus.FAIL || isExamFailedButCodeNowLater);

    licenseFeeReceiptNoQuestion.visible = flag;
    paymentTypeQuestion.visible = flag;
  }, [currentExam, enableProvideExistingLicense, examData, survey]);

  const form = useMemo(() => {
    if (pageInitiating) return;
    survey.showPageTitles = false;
    survey.showNavigationButtons = "none";
    survey.questionErrorLocation = "bottom";
    survey.showCompletedPage = false;
    survey.setValue("noChineseName", formValues.noChineseName); // PHKL Custom
    survey.mergeData({ ...survey.data, ...formValues });
    survey.onComplete.add((_sender: any) => {});
    survey.onTextMarkdown.add(markdownHandler);
    survey.onGetQuestionTitleActions.add((_: any, options: any) => {
      if (options.question.tooltips) {
        options.titleActions = [
          {
            id: "show-popover",
            component: "popover",
            tooltip: options.question.tooltips,
          },
        ];
      }
    });
    setExamData(survey.data);
    survey.onValueChanged.add(async (_sender: any, options: any) => {
      setExamData({ ...survey.data, [options.name]: options.value });

      if (options.name === "examScheduleId") {
        const remainingQuotaQuestion = survey.getQuestionByName("remainingQuota");
        const examFeeQuestion = survey.getQuestionByName("examFee");
        const startRegistrationDateQuestion = survey.getQuestionByName("startRegistrationDate");
        const lastRegistrationTimeQuestion = survey.getQuestionByName("lastRegistrationTime");
        const addressQuestion = survey.getQuestionByName("address");
        const remarksQuestion = survey.getQuestionByName("remarks");
        var examScheduleId = options.value;
        const examTimeslotPool = examTimeslotPools.find((obj) => obj.value === examScheduleId);

        if (examScheduleId) {
          remainingQuotaQuestion.value = examTimeslotPool?.remainingQuota;
          examFeeQuestion.value = examTimeslotPool?.examFee;
          startRegistrationDateQuestion.value = initCommonDate(
            examTimeslotPool?.startRegistrationDate,
          ); // PHKL Custom
          lastRegistrationTimeQuestion.value = initCommonDate(
            examTimeslotPool?.lastRegistrationDate,
          ); // PHKL Custom
          addressQuestion.value = examTimeslotPool?.address;
          remarksQuestion.value = examTimeslotPool?.remarks;
        }
      }
      /* PHKL Custom s */
      if (options.name === "noChineseName") {
        const localName = survey.getQuestionByName("localName");
        if (!options.value) {
          localName.isRequired = true;
          localName.readOnly = false;
        } else {
          localName.isRequired = false;
          localName.value = "";
          localName.readOnly = true;
        }
      }
      /* PHKL Custom e */
    });

    survey.onCurrentPageChanged.add((_sender: any, options: any) => {
      // PHKL Custom -s
      const noChineseName = survey.getQuestionByName("noChineseName");
      const localName = survey.getQuestionByName("localName");
      if (noChineseName.value) {
        localName.isRequired = false;
        localName.value = "";
        localName.readOnly = true;
      } else {
        localName.isRequired = true;
        localName.readOnly = false;
      }
      // PHKL Custom -e
      const currentPageNo = survey.currentPageNo;
      setCurrentPageNo(currentPageNo);
    });

    // PHKL Custom -s
    if (isApprovalPage) {
      const page1 = survey.getPageByName("page1");
      page1.elements.forEach((question: any) => {
        question.readOnly = true;
      });
    } else {
      survey.getAllQuestions().forEach((question: any) => {
        question.readOnly = surveyIsReadonly;
        if (question.name === "paymentType") {
          question.readOnly = getPaymentType() ? true : false;
        }
        const hiddenQuestions = [
          "personalInfoAgreement1",
          "personalInfoAgreement2",
          "personalInfoAgreement3",
        ];
        if (surveyIsReadonly) {
          if (hiddenQuestions.includes(question.name)) {
            question.visible = false;
          }
        }
      });
    }
    // PHKL Custom -e

    initFileUploadAndDownLoadAndClearForLicenseExam(survey);
    return (
      <div className="flex  bg-gray-100 flex-col   pb-4">
        <div
          className="flex pt-5 pl-5 pr-4 text-xl font-semibold line-height-28"
          style={{ flexWrap: "wrap" }}
        >
          <span
            style={{
              overflowWrap: "break-word",
              overflow: "hidden",
              whiteSpace: "normal",
              maxWidth: "100%",
            }}
          >
            {currentExam?.name}
          </span>
        </div>
        <div className="bg-gray-100  flex justify-center items-center pt-5">
          <Survey
            currentPageNo={0}
            className="survey-common"
            id="training-payment"
            model={survey}
          />
        </div>
      </div>
    );
  }, [formValues, pageInitiating, survey, surveyIsReadonly, examTimeslotPools]);

  const bottom = useMemo(() => {
    if (!survey) {
      return;
    }
    return (
      <div className="fixed bottom-0 left-0 right-0 py-4 px-4 bg-gray-100 z-50">
        <div className=" flex justify-center">
          <ConfirmButton
            disabled={isSubmitting} /*PHKL Custom*/
            onClick={async () => {
              let isValid = false;
              isValid = survey.validateCurrentPage();
              if (!isValid) {
                return;
              }

              // PHKL Custom -s
              if (isApprovalPage) {
                //Approve btn
                if (survey.currentPageNo === 0) {
                  if (await onApproveVerify(examData)) {
                    if (enableLicenseExamPayment) {
                      survey.currentPageNo = 1;
                    } else {
                      survey.currentPageNo = 2;
                    }
                  }
                  //Next btn
                } else if (survey.currentPageNo === 1) {
                  survey.currentPageNo = 2;
                  //Register btn
                } else if (survey.currentPageNo === 2) {
                  approveOnSubmitHandler(examData);
                }
                return;
              } else if (surveyIsReadonly) {
                navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/candidate-home`);
                backToApp();
              }
              // PHKL Custom -e

              const isLastPage = currentPageNo === survey.visiblePageCount - 1;
              const isExamPassed = examData?.hasPassedExam;
              if (!isExamPassed && !isLastPage) {
                if (enableLicenseExamPayment && survey.currentPageNo !== 1) {
                  //page0
                  survey.currentPageNo = 1;
                } else {
                  if (enableLicenseExamPayment && !surveyIsReadonly) {
                    const proceed = await validateData(examData);
                    if (proceed) {
                      survey.currentPageNo = 2;
                    }
                  } else {
                    survey.currentPageNo = 2;
                  }
                }
              } else {
                if (surveyIsReadonly) {
                  navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/candidate-home`);
                  backToApp();
                } else {
                  setIsSubmitting(true); //PHKL Custom
                  onSubmitHandler(examData);
                }
              }
            }}
          >
            {confirmButtonText}
          </ConfirmButton>
          {/* PHKL Custom -s */}
          {isApprovalPage && survey?.currentPageNo === 0 && (
            <CancelButton variant="outlined" onClick={onReject}>
              {t("app.button.reject")}
            </CancelButton>
          )}
          {/* PHKL Custom -e */}
        </div>
      </div>
    );
  }, [
    survey,
    confirmButtonText,
    currentPageNo,
    examData,
    enableLicenseExamPayment,
    validateData,
    onSubmitHandler,
    isSubmitting, //PHKL Custom
  ]);

  return (
    <div className={`${styles.profileContainer} flex flex-col min-h-screen bg-gray-100`}>
      {isPortrait ? (
        <HeaderComponent
          title={
            //PHKL Custom -s
            // survey?.currentPageNo === 2
            //   ? t("candidate_data")
            //   :
            //PHKL Custom -e
            surveyIsReadonly ? t("exam_detail") : t("new_registration")
          }
          onBack={() => {
            if (survey.currentPageNo === 2 && enableLicenseExamPayment) {
              survey.currentPageNo = 1;
            } else if (survey.currentPageNo > 0) {
              survey.currentPageNo = 0;
            } else {
              if (canGoBack()) navigate(-1);
            }
          }}
        />
      ) : (
        <span className={styles.tips}>
          {/* PHKL Custom -s */}
          <ArrowBack
            style={{ cursor: "pointer", fontSize: 40, paddingRight: 10 }}
            onClick={() => {
              if (survey.currentPageNo === 2 && enableLicenseExamPayment) {
                survey.currentPageNo = 1;
              } else if (survey.currentPageNo > 0) {
                survey.currentPageNo = 0;
              } else {
                if (canGoBack()) navigate(-1);
              }
            }}
          />
          {/* PHKL Custom -e */}

          {
            //PHKL Custom -s
            // survey?.currentPageNo === 2
            //   ? t("candidate_data")
            //   :
            //PHKL Custom -e
            surveyIsReadonly ? t("exam_detail") : t("new_registration")
          }
        </span>
      )}
      <div className="flex-grow overflow-auto" style={{ marginBottom: "80px" }}>
        {form}
        {/* PHKL Custom -s */}
        {pageInitiating && (
          <div className="flex flex-1 items-center justify-center">
            <CircularProgress sx={{ color: color.majorRed }} />
          </div>
        )}
        {/* PHKL Custom -e */}
        {bottom}
      </div>
      {bottom}
    </div>
  );
};

const convertTemplate = (_template: any, recruitConfigs: any, globalConfigs: any) => {
  const template = cloneDeep(_template);
  const elements = template.pages[2].elements;
  const examPageElements = template.pages[0].elements;
  const enableExamFeeInput = get(recruitConfigs, "enableExamFeeInput", false);
  // remove exam fee from the elements if not enabled
  if (!enableExamFeeInput) {
    const examFeeField = examPageElements.find((item: any) => item.name === "examFee");

    if (examFeeField) {
      examFeeField.visibleIf = undefined;
      examFeeField.visible = false;
    }
  }
  const regionPhoneField = elements.find((item: any) => item.name === "regionPhone");
  regionPhoneField.readOnly = true;
  regionPhoneField.templateElements[0].readOnly = true;
  regionPhoneField.templateElements[1].readOnly = true;

  // add max and min birthday
  const birthdayField = template.pages[2].elements.find((item: any) => item.name === "birthday");
  birthdayField.visible = true;
  if (!recruitConfigs?.candidateProfile?.enableBirthday) {
    birthdayField.visible = false;
  } else if (globalConfigs?.region.ageValidation) {
    const { min, max } = globalConfigs?.region?.ageValidation;
    const minDate = max?.value
      ? moment().subtract(max.value, "years").format("YYYY-MM-DD")
      : undefined;
    const maxDate = min?.value
      ? moment().subtract(min.value, "years").format("YYYY-MM-DD")
      : undefined;

    birthdayField.min = minDate;
    birthdayField.max = maxDate;
  }

  // Function to translate a given field if it exists
  const translateField = (obj: any, field: string) => {
    if (obj?.[field]) {
      obj[field] = getI18n().t(obj[field]);
    }
  };
  // Loop through each element in the first page
  template.pages.forEach((page: any) => {
    page.elements = page.elements.map((q: any) => {
      translateField(q, "title");
      translateField(q, "placeholder");
      translateField(q, "labelTrue");
      translateField(q, "labelFalse");
      translateField(q, "requiredErrorText");
      translateField(q, "tooltips");
      translateField(q, "description");
      translateField(q?.modalConfig, "modalTitle");
      // translate choices if they exist
      if (q.choices && Array.isArray(q.choices)) {
        q.choices.forEach((choice: any) => {
          translateField(choice, "text");
        });
      }
      //PHKL Custom -s
      if (q?.type === "text") {
        q.multiline = true;
      }
      //PHKL Custom -e

      if (q?.type === "html") {
        const htmlString = q.html;
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = htmlString;
        const paragraphs = tempDiv.getElementsByTagName("p");
        Array.from(paragraphs).forEach((p) => {
          const content = translate((p.textContent as string).trim());
          p.textContent = content;
        });
        const newHtmlString = tempDiv.innerHTML;
        q.html = newHtmlString;
      }

      // Check if the element has validators and translate their text fields
      if (q.validators && Array.isArray(q.validators)) {
        q.validators.forEach((validator: any) => {
          translateField(validator, "text");
        });
      }

      // If the element is a paneldynamic type, we need to handle nested templateElements
      if (q.type === "paneldynamic" && q.templateElements && Array.isArray(q.templateElements)) {
        q.templateElements = q.templateElements.map((te: any) => {
          translateField(te, "title");
          translateField(te, "labelTrue");
          translateField(te, "labelFalse");
          translateField(te, "requiredErrorText");
          translateField(te, "placeholder");
          translateField(q, "description");
          translateField(te?.modalConfig, "modalTitle");

          if (te.validators && Array.isArray(te.validators)) {
            te.validators.forEach((validator: any) => {
              translateField(validator, "text");
            });
          }

          return te;
        });
      }

      return q;
    });
  });
  return template;
};
